import React, { useState, useEffect, useMemo } from "react";
import { Route, Router, Routes, useLocation } from "react-router-dom";
import SignInPage from "./pages/SignInPage";
import ErrorPage from "./pages/ErrorPage";
import userRoutes from "./userRoutes";
import { getRolehasPermissions } from "./server/rolesAllApi";
import "./App.css";
import MasterLayout from "./masterLayout/MasterLayout";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import ProtectedRoute from "./ProtectedRoute";
import PaypalButton from "./pages/Paypal/PaypalButton";
import PaymentSuccess from "./pages/Paypal/PaymentSuccess";
import PaymentDeclined from "./pages/Paypal/PaymentDeclined";

// Simple loading component
const LoadingComponent = () => (
  <div className="loading-container">
    <p>Loading...</p>
    {/* Add a spinner or custom styling here */}
  </div>
);

export default function App() {
  const { pathname } = useLocation();
  const [routes, setRoutes] = useState(userRoutes); // Initialize with userRoutes
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState(
    JSON.parse(localStorage.getItem("userPermissions")) || []
  );
  const token = localStorage.getItem("token");

  // Fetch user details only once on mounta
  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = localStorage.getItem("token");

      if (user && token) {
        setIsAuthenticated(true);
        if (user.roleId) {
          try {
            const response = await getRolehasPermissions(user.roleId);
            const permissions = response?.data || [];

            // ✅ Save userPermissions in localStorage
            localStorage.setItem("userPermissions", JSON.stringify(permissions));

            setUserPermissions(permissions);
            // console.log("User Permissions: ", permissions);
            
          } catch (error) {
            console.error("Error fetching permissions:", error);
            setUserPermissions([]); // Fallback to empty permissions
          }
        }
      } else {
        setIsAuthenticated(false);
      }
      setRoutes(userRoutes); // Ensure routes are set
      setLoading(false);
    };

    fetchUserDetails();
  }, [token]); // Empty dependency array: run once on mount

  // Filter routes based on permissions
  const filterRoutes = (allRoutes, permissions) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        const filteredCollapse = filterRoutes(route.collapse, permissions);
        return filteredCollapse.length
          ? [{ ...route, collapse: filteredCollapse }]
          : [];
      }
      return !route.permissionName || permissions?.includes(route.permissionName)
        ? [route]
        : [];
    });

  const filteredRoutes = useMemo(
    () => filterRoutes(routes, userPermissions),
    [routes, userPermissions]
  );

  // Generate Route components from filtered routes
  const getRoutes = (allRoutes) =>
    allRoutes?.flatMap((route, index) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      return route.route ? (
        <Route
          key={route.key || index}
          path={route.route}
          element={<route.component />}
        />
      ) : (
        []
      );
    });

  return (
    <>
      <RouteScrollToTop />
      <Routes>
        {/* SignInPage route without MasterLayout */}
        <Route path="/" element={<SignInPage />} />
        <Route path="/paypal/:token" element={<PaypalButton />} />
        <Route path="/paypal/success" element={<PaymentSuccess />} />
        <Route path="/paypal/failed" element={<PaymentDeclined />} />

        {/* All other routes wrapped with MasterLayout */}
        <Route
          path="/*"
          element={
            <MasterLayout>
              <Routes>
                {routes
                  .filter((route) => route.route !== "/") // Exclude SignInPage
                  .map((route, index) =>
                    route.permissionName ? (
                      <Route
                        key={index}
                        path={route.route}
                        element={
                          <ProtectedRoute
                            element={route.component}
                            permissionName={route.permissionName}
                            userPermissions={userPermissions}
                          />
                        }
                      />
                    ) : (
                      <Route
                        key={index}
                        path={route.route}
                        element={<route.component />}
                      />
                    )
                  )}
              </Routes>
            </MasterLayout>
          }
        />
      </Routes>
    </>
  );
}